import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../hooks/use-toast';
import {
  Rocket, TrendingUp, Zap,
  AlertCircle, CheckCircle, Sparkles, History,
  ArrowLeft, Loader2, Eye, Phone, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Utility functions
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 0
  }).format(price);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// PackageCard component
const PackageCard = ({ pkg, onSelect, listingId, isProcessing, navigate }) => {
  return (
    <Card className={`relative overflow-hidden transition-all hover:shadow-lg ${
      pkg.popular ? 'border-2 border-blue-500 ring-2 ring-blue-100' :
      pkg.best_value ? 'border-2 border-green-500 ring-2 ring-green-100' : ''
    }`}>
      {pkg.popular && (
        <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
          Most Popular
        </div>
      )}
      {pkg.best_value && (
        <div className="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
          Best Value
        </div>
      )}
      <CardContent className="p-6">
        <div className="text-center mb-4">
          <div className={`w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center ${
            pkg.best_value ? 'bg-green-100' : pkg.popular ? 'bg-blue-100' : 'bg-purple-100'
          }`}>
            <Rocket className={`w-7 h-7 ${
              pkg.best_value ? 'text-green-600' : pkg.popular ? 'text-blue-600' : 'text-purple-600'
            }`} />
          </div>
          <h3 className="text-xl font-bold">{pkg.name}</h3>
          <p className="text-sm text-gray-500">{pkg.description}</p>
        </div>
        
        <div className="text-center mb-4">
          <p className="text-3xl font-bold text-gray-900">{formatPrice(pkg.price)}</p>
          <p className="text-sm text-gray-500">for {pkg.duration_days} days</p>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>{pkg.multiplier}x visibility boost</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Priority in search results</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Featured badge on ad</span>
          </div>
        </div>

        {listingId ? (
          <Button
            className="w-full"
            onClick={() => onSelect(listingId, pkg.package_key)}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
            ) : (
              <><Zap className="w-4 h-4 mr-2" /> Boost Now</>
            )}
          </Button>
        ) : (
          <Button className="w-full" variant="outline" onClick={() => navigate('/dashboard')}>
            Select Ad to Boost
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// RecommendationCard component
const RecommendationCard = ({ rec, packages, processingBoost, onBoost }) => {
  const [selectedPackage, setSelectedPackage] = useState(rec.recommended_package);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex gap-4">
          <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
            {rec.thumbnail ? (
              <img src={rec.thumbnail} alt={rec.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <Target className="w-8 h-8" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-semibold truncate">{rec.title}</h4>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {rec.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {rec.contacts}
                  </span>
                  <span>{rec.conversion_rate}% conv.</span>
                </div>
              </div>
              <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                {rec.priority} priority
              </Badge>
            </div>

            <p className="text-sm text-gray-600 mt-2">{rec.reason}</p>

            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-green-600">{rec.predicted_boost}</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  className="text-sm border rounded px-2 py-1"
                >
                  {packages.map(p => (
                    <option key={p.package_key} value={p.package_key}>
                      {p.name} - {formatPrice(p.price)}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={() => onBoost(rec.listing_id, selectedPackage)}
                  disabled={processingBoost === `${rec.listing_id}_${selectedPackage}`}
                >
                  {processingBoost === `${rec.listing_id}_${selectedPackage}` ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Boost'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BoostOptimizer = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [boostHistory, setBoostHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('recommend');
  const [processingBoost, setProcessingBoost] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const packagesRes = await fetch(`${API_URL}/api/boost/packages`);
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        setPackages(data.packages || []);
      }

      if (token) {
        const recsRes = await fetch(`${API_URL}/api/boost/recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (recsRes.ok) {
          const data = await recsRes.json();
          setRecommendations(data.recommendations || []);
        }

        const histRes = await fetch(`${API_URL}/api/boost/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (histRes.ok) {
          const data = await histRes.json();
          setBoostHistory(data.boosts || []);
        }

        const analyticsRes = await fetch(`${API_URL}/api/boost/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (analyticsRes.ok) {
          const data = await analyticsRes.json();
          setAnalytics(data);
        }
      }
    } catch (err) {
      console.error('Error fetching boost data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBoostAd = async (listingId, packageKey) => {
    if (!token) {
      navigate('/login');
      return;
    }

    setProcessingBoost(`${listingId}_${packageKey}`);

    try {
      const response = await fetch(`${API_URL}/api/payments/boost/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          listing_id: listingId,
          package_key: packageKey,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to create checkout',
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to process boost request',
        variant: 'destructive'
      });
    } finally {
      setProcessingBoost(null);
    }
  };

  const tabs = useMemo(() => [
    { id: 'recommend', label: 'AI Recommendations', icon: Sparkles },
    { id: 'packages', label: 'Boost Packages', icon: Rocket },
    { id: 'history', label: 'Boost History', icon: History }
  ], []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-10 w-64 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/dashboard" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Rocket className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Boost Optimizer</h1>
              <p className="text-purple-200">AI-powered recommendations to maximize your ad visibility</p>
            </div>
          </div>

          {analytics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-purple-200 text-sm">Total Boosts</p>
                <p className="text-2xl font-bold">{analytics.total_boosts}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-purple-200 text-sm">Active Boosts</p>
                <p className="text-2xl font-bold">{analytics.active_boosts}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-purple-200 text-sm">Total Spent</p>
                <p className="text-2xl font-bold">{formatPrice(analytics.total_spent)}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-purple-200 text-sm">Boost Days</p>
                <p className="text-2xl font-bold">{analytics.total_boost_days}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className="flex-shrink-0"
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* AI Recommendations Tab */}
        {activeTab === 'recommend' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                AI Boost Recommendations
              </CardTitle>
              <CardDescription>
                Our AI analyzes your ad performance and suggests which ads to boost for maximum impact
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!token ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Login to see personalized recommendations</p>
                  <Link to="/login">
                    <Button>Login</Button>
                  </Link>
                </div>
              ) : recommendations.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No recommendations yet</p>
                  <p className="text-sm text-gray-400">Post some ads to get AI-powered boost suggestions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map(rec => (
                    <RecommendationCard 
                      key={rec.listing_id} 
                      rec={rec} 
                      packages={packages}
                      processingBoost={processingBoost}
                      onBoost={handleBoostAd}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <Card>
            <CardHeader>
              <CardTitle>Boost Packages</CardTitle>
              <CardDescription>
                Choose a package to increase your ad visibility and get more buyers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {packages.map(pkg => (
                  <PackageCard 
                    key={pkg.package_key} 
                    pkg={pkg} 
                    navigate={navigate}
                    onSelect={handleBoostAd}
                    listingId={null}
                    isProcessing={false}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Boost History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!token ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Login to see your boost history</p>
                  <Link to="/login">
                    <Button>Login</Button>
                  </Link>
                </div>
              ) : boostHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No boost history yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm font-medium text-gray-500">Package</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-500">Amount</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-500">Start Date</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-500">End Date</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boostHistory.map(boost => (
                        <tr key={boost.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div className="font-medium">{boost.package_name}</div>
                            <div className="text-sm text-gray-500">{boost.multiplier}x boost</div>
                          </td>
                          <td className="p-3 font-medium">{formatPrice(boost.price)}</td>
                          <td className="p-3 text-sm">{formatDate(boost.start_date)}</td>
                          <td className="p-3 text-sm">{formatDate(boost.end_date)}</td>
                          <td className="p-3">
                            <Badge variant={boost.is_active ? 'default' : 'secondary'}>
                              {boost.is_active ? `${boost.days_remaining} days left` : 'Expired'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BoostOptimizer;
